import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import _ from "lodash";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { Divider as MuiDivider, Typography, Card, CardContent, Button } from "@material-ui/core";
import { spacing } from "@material-ui/system";
import MyRadonLineChart from "./MyRadonLineChart";
import MyRadonInterface from "../../integration/internal/MyRadon/MyRadonInterface";
import { SUCCESS } from "../../constants/resultCodes";
import { enqueueSnackbar } from "notistack";
import moment from "moment-timezone";
import {
  DISCLAIMER,
  mitigationSystem,
  singleBuildingType,
  structureType,
  testFloor,
  TIMEZONE_MAP
} from "../../constants/values";
import Loading from "../components/Loading";
import Space from "../../components/Space";
import { ExpandMore, ExpandLess } from "@material-ui/icons";

import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import Dialog from "@material-ui/core/Dialog";
import AnalysisInProgressDialog from "../../components/AnalysisInProgressDialog";
import RadonLevelsGuideDialog from "../../components/RadonLevelsGuideDialog";
import WeatherGuideDialog from "../../components/WeatherGuideDialog";

const TableContainer = styled.div`
  max-height: ${({ expanded }) => (expanded ? "none" : "180px")};
  overflow: hidden;
`;

const ExpandButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 8px;
`;

const Divider = styled(MuiDivider)(spacing);

const InfoButton = styled(Button)`
  min-width: 18px;
  width: 18px;
  height: 18px;
  padding: 0;
  font-weight: bold;
  border-radius: 50%;
  font-size: 12px;
  line-height: 18px;
  color: #4a4a4a;
  border: 1px solid #4a4a4a;
  margin-left: 8px;
`;

const Container = styled.div`
  max-width: 800px;
  margin: auto;
  padding: 0;
`;

const SectionTitle = styled(Typography)`
  font-weight: bold;
  margin-bottom: 8px;
`;

const RecommendTitle = styled(Typography)`
  font-weight: bold;
  margin-bottom: 8px;
  color: #D65A5A; /* 또는 #D32F2F */
`;

const BulletPoint = styled(Typography)`
  margin-left: 16px;
  margin-bottom: 4px;
`;

// const StyledRow = styled.div`
//   display: flex;
//   justify-content: space-between;
//   padding: 8px 0;
//   border-bottom: 1px solid lightgray;
// `;

const StyledRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center; 
  padding: 8px 0;
  border-bottom: 1px solid lightgray;
`;

const KeyText = styled(Typography)`
  // font-weight: bold;
`;

const ValueText = styled(Typography)`
  text-align: right;
`;

const CustomList = styled.ul`
  margin-top: 4px;
  padding-left: 0;
  list-style: none;
`;

const CustomListItem = styled.li`
  position: relative;
  padding-left: 20px; /* 텍스트 위치 조정 */
  padding-bottom: 4px;
  padding-top: 0px;
`;

const CustomBullet = styled.span`
  position: absolute;
  left: 5px; /* BulletPoint 왼쪽 이동 */
  top: 6px;
  width: 5px;
  height: 5px;
  background-color: gray;
  border-radius: 50%;
`;

const CustomText = styled.span`
  color: #4a4a4a; /* 어두운 회색 */
  font-size: 14px;
`;

const SurveyTitle = styled(Typography)`
  color: #36b5f5;
  font-weight: 700;
  margin-bottom: 12px;
`;

const SurveyDescription = styled(Typography)`
  color: #1f1f1f;
  line-height: 1.45;
  margin-bottom: 16px;
`;

const SurveyButton = styled(Button)`
  background-color: #2daef2;
  color: white;
  text-transform: none;
  border-radius: 999px;
  min-width: 0;
  min-height: 0;
  line-height: 1;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 500;

  &:hover {
    background-color: #1e9de0;
  }

  &:active {
    background-color: #167fbd;
  }
`;

const SurveyTopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  margin: 8px 6px 12px;
`;

const SurveyTopText = styled(Typography)`
  color: #6a74a1;
  font-size: 12px;
  line-height: 1.2;
`;

const SurveyTopLink = styled.a`
  color: #1cb6ff;
  font-size: 12px;
  line-height: 1.2;
  text-decoration: underline;
`;

function Report() {
  const location = useLocation();
  const [disclaimer, setDisclaimer] = useState(DISCLAIMER);
  const [openRadonLevelsGuideDialog, setOpenRadonLevelsGuideDialog] = useState(false);
  const [openWeatherGuideDialog, setOpenWeatherGuideDialog] = useState(false);
  const handleCloseRadonLevelsGuideDialog = () => {
    setOpenRadonLevelsGuideDialog(false);
  };
  const handleCloseWeatherGuideDialog = () => {
    setOpenWeatherGuideDialog(false);
  };

  const [summary, setSummary] = useState({
    'deviceName': '',
    'avg72Hour': '',
    'location': '',
    'mitigationInstalled': '',
    'headline': '',
    'details': []
  });

  const [measurement, setMeasurement] = useState({
    'state': '',
    'measurementStartTime': '',
    'measurementEndTime': '',
    'location': '',
    'highest': '',
    'average': '',
    'lowest': '',
  });

  const [above, setAbove] = useState();
  const [compare, setCompare] = useState({
    'testAvg': '',
    'stateAvg': ''
  });
  const [graphData, setGraphData] = useState([]);

  const [pieData, setPieData] = useState({
    lowCount: 0,
    mediumCount: 0,
    highCount: 0,
    lowPercentage: '0.0',
    mediumPercentage: '0.0',
    highPercentage: '0.0',
  });

  const [loading, setLoading] = useState(true); // 로딩 상태 추가
  let { email, reportId } = location.state || {};

  // TEST DATA
  if (!email) {
    email = localStorage.getItem("0xaccountId");
  }

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const loadReportData = async () => {
      try {
        setLoading(true); // 데이터 로딩 시작
        let res = await MyRadonInterface.getMyRadonDetails(email, reportId);
        //console.log('res = ' + JSON.stringify(res));

        let data = _.get(res, "data", []);
        if (!data || data.length === 0) {
          throw new Error("No data error");
          setLoading(false);
          return;
        }

        /* Common */
        const BqToPci = (Bq) => (Bq * 0.027).toFixed(1); // Bq -> pCi/L 변환 공식

        // 주소 정보
        const street = _.get(data[0], "master.location.street", '');
        const city = _.get(data[0], "master.location.city", '');
        let state = _.get(data[0], "master.location.state", '');
        let countryCode = _.get(data[0], "master.location.countryCode", '');
        let country = _.get(data[0], "master.location.country", '');
        if (!countryCode && country) {
          const countryUpper = country.trim().toUpperCase();
          if (
            countryUpper === "US" ||
            countryUpper === "U.S" ||
            countryUpper === "USA" ||
            countryUpper === "U.S.A" ||
            countryUpper === "UNITED STATES" ||
            countryUpper === "UNITED STATES OF AMERICA"
          ) {
            countryCode = "US"
          }
        }

        //console.log('countryCode = ' + countryCode);

        if (countryCode) {
          countryCode = countryCode.toUpperCase();
        }

        if (countryCode !== 'US') {
          alert('The country in the address of the generated report must be the United States. The current country code set for the address is shown below. The currently set CountryCode is ' + countryCode);
          return;
        }

        if (state) {
          state = state.toUpperCase();
        }


        /* Set Time Zone */
        let timezone = "America/New_York"; // Default timezone
        if (state) {
          const masterInfo = _.get(data[0], "master", {});
          timezone = TIMEZONE_MAP[state];
        }

        if (!timezone) {
          timezone = TIMEZONE_MAP['CA'];
        }

        /* Set Summary Data */
        const deviceName = _.get(data[0], "device_name", '');
        let avg72Hour = _.get(data[0], "analysis_radon.avg", '');
        let locationFloor = _.get(data[0], "master.placement.location_floor", '');
        let mitigationInstalled = _.get(data[0], "master.placement.mitigation_installed", '');
        let headline = _.get(data[0], "recommendations.headline", '');
        let details = _.get(data[0], "recommendations.details", []);

        // 1. Convert avg72Hour (Bq) to pCi/L and round to one decimal place
        const avg72HourInPCiL = avg72Hour ? BqToPci(avg72Hour) : '';

        // 2. Convert location code to structureType string
        const locationStr = testFloor[locationFloor] || locationFloor; // Defaults to the location code if not found in structureType

        // 3. Convert mitigationInstalled value to appropriate string
        const mitigationInstalledStr = mitigationSystem[mitigationInstalled] || 'N/A'; // Defaults to 'N/A' if the value is undefined or invalid

        const summaryData = {
          'deviceName': deviceName,
          'avg72Hour': avg72HourInPCiL,  // pCi/L value with 1 decimal place
          'location': locationStr,  // Converted location type
          'mitigationInstalled': mitigationInstalledStr,  // Converted mitigation status
          'headline': headline,
          'details': details
        };

        let measurementStartTime = _.get(data[0], "analysis_radon.duration.start", '')/1000;
        let measurementEndTime = _.get(data[0], "analysis_radon.duration.end", '')/1000;

        if (measurementStartTime) {
          measurementStartTime = moment(measurementStartTime).tz(timezone).format('MM/DD/YY hh:mm a');
        }

        if (measurementEndTime) {
          measurementEndTime = moment(measurementEndTime).tz(timezone).format('MM/DD/YY hh:mm a');
        }

        // 주소 처리 로직
        let address = '';
        if (street && city) {
          address = street + ', ' + city + ', ' + state;
        } else if (street && !city) {
          address = street + ', ' + state;
        } else if (!street && city) {
          address = city + ', ' + state;
        } else if (!street && !city && state) {
          address = state;
        }

        // Radon 수치 정보
        const highest = _.get(data[0], "analysis_radon.highest_radon.radon_level", '');
        const highestTimeStamp = _.get(data[0], "analysis_radon.highest_radon.timestamp", '');
        const highestTime = highestTimeStamp ? moment(highestTimeStamp/1000).tz(timezone).format('MM/DD/YY hh:mm a') : '';
        const lowest = _.get(data[0], "analysis_radon.lowest_radon.radon_level", '');
        const lowestTimeStamp = _.get(data[0], "analysis_radon.lowest_radon.timestamp", '');
        const lowestTime = lowestTimeStamp ? moment(lowestTimeStamp/1000).tz(timezone).format('MM/DD/YY hh:mm a'): '';

        const highestInPci = highest ? BqToPci(highest) : '';
        const lowestInPci = lowest ? BqToPci(lowest) : '';

        const measurementData = {
          'measurementStartTime': measurementStartTime,
          'measurementEndTime': measurementEndTime,
          'location': address,
          'highest': highestInPci,
          'highestTime': highestTime,
          'average': avg72HourInPCiL,
          'lowest': lowestInPci,
          'lowestTime': lowestTime,
          'state': state
        }

        /* Set Above */
        let aboveData = _.get(data[0], "analysis_radon.high_readings", []);
        const transformedAboveData = aboveData.map((entry) => {
          const radonLevelInPci = BqToPci(entry.radon_level); // Bq -> pCi/L 변환
          const formattedTime = moment(entry.timestamp/1000).tz(timezone).format('MM/DD/YY hh:mm a'); // 시간 포맷 변경

          return {
            date: formattedTime.split(' ')[0], // 날짜 부분
            time: formattedTime.split(' ')[1] + ' ' + formattedTime.split(' ')[2], // 시간 부분
            value: `${radonLevelInPci} pCi/L`, // 변환된 라돈 수치
          };
        });

        /* Set Compare */
        let stateAvg = _.get(data[0], "community_radon.state_avg", '');
        const stateAvgInPci = stateAvg ? BqToPci(stateAvg) : 'N/A';
        const compareData = {
          'testAvg': avg72HourInPCiL,
          'stateAvg': stateAvgInPci
        }

        // data[0].collected.sort((a, b) => a.timestamp - b.timestamp);
        // data[0].collected[2].radon_level = '';
        // data[0].collected[4].radon_level = '';
        // data[0].collected[6].radon_level = '';

        //console.log('1: data[0].collected = ' + JSON.stringify(data[0].collected));

        // 먼저 원본 데이터를 변형하지 않고 flatMap으로 펼침
        const flattenedData = data.flatMap((report) =>
          report.collected.map((entry) => {
            const {
              weather = {},
              radon_level = null,
              timestamp = 0,
              device_location = {},
            } = entry;

            const { dt, tmp, hmd, wnd_sp, prs, main } = weather;
            const { state } = device_location;

            const localTime = moment.unix(Math.floor(timestamp / 1e6)).tz(timezone);

            const radonValue = (radon_level !== null && radon_level !== '')
              ? parseFloat((radon_level / 37).toFixed(2))
              : null;

            return {
              name: localTime.format("MM/DD/YYYY hh:mm A"),
              time: localTime.format("hh:mm"),
              period: localTime.format("A"),
              radonValue,
              temperature: tmp !== null && tmp !== undefined ? parseFloat(tmp) : null,
              humidity: hmd !== null && hmd !== undefined ? parseFloat(hmd) : null,
              windSpeed: wnd_sp !== null && wnd_sp !== undefined ? parseFloat(wnd_sp) : null,
              pressure: prs !== null && prs !== undefined ? parseFloat(prs) : null,
              weather: main,
              timestamp
            };
          }))
          .filter((entry) => entry !== null)
          .sort((a, b) => a.timestamp - b.timestamp);

        // 보간 처리: adjustedRadonValue만 설정
        function setAdjustedRadonValues(data) {
          // 원본 데이터를 수정하지 않도록 복사본 생성
          const result = data.map(entry => ({ ...entry, adjustedRadonValue: null }));

          for (let i = 0; i < result.length; i++) {
            const current = result[i];

            // 경우 1: radonValue가 null이 아니면 adjustedRadonValue를 동일한 값으로 설정
            if (current.radonValue !== null) {
              current.adjustedRadonValue = current.radonValue;
              continue;
            }

            // 경우 2: radonValue가 null이면 이전 및 다음 비-null 값 찾기
            let prevValue = null;
            let nextValue = null;

            // 이전 비-null radonValue 찾기
            for (let j = i - 1; j >= 0; j--) {
              if (result[j].radonValue !== null) {
                prevValue = result[j].radonValue;
                break;
              }
            }

            // 다음 비-null radonValue 찾기
            for (let j = i + 1; j < result.length; j++) {
              if (result[j].radonValue !== null) {
                nextValue = result[j].radonValue;
                break;
              }
            }

            // 경우 3 & 4: 이전 및 다음 값이 null인 경우 처리
            // 이전값과 다음값 모두 null이면 adjustedRadonValue를 0으로 설정
            if (prevValue === null && nextValue === null) {
              current.adjustedRadonValue = 0;
            }
            // 이전값만 null이면 이전값을 0으로 사용
            else if (prevValue === null) {
              current.adjustedRadonValue = (0 + nextValue) / 2;
            }
            // 다음값만 null이면 다음값을 0으로 사용
            else if (nextValue === null) {
              current.adjustedRadonValue = (prevValue + 0) / 2;
            }
            // 이전값과 다음값 모두 존재하면 평균 계산
            else {
              current.adjustedRadonValue = (prevValue + nextValue) / 2;
            }

            // 일관성을 위해 소수점 2자리로 반올림
            if (current.adjustedRadonValue !== null) {
              current.adjustedRadonValue = parseFloat(current.adjustedRadonValue.toFixed(2));
            }
          }

          return result;
        }

        const processedData = setAdjustedRadonValues(flattenedData);
        processedData.sort((a, b) => a.timestamp - b.timestamp);
        //console.log('processedData = ' + JSON.stringify(processedData));

        // 최종 데이터 변환
        const transformedGraphData = processedData
          .map((entry) => {
            const {
              name,
              time,
              period,
              radonValue,
              adjustedRadonValue,
              temperature,
              humidity,
              windSpeed,
              pressure,
              weather,
              timestamp,
            } = entry;

            return {
              name,
              time,
              period,
              value0: adjustedRadonValue, // 보간값
              value1: radonValue,         // 원래값
              value2: temperature,
              value3: humidity,
              value4: windSpeed,
              value5: pressure,
              weather,
              timestamp
            };
          })
          .filter((entry) => entry !== null)
          .sort((a, b) => a.timestamp - b.timestamp);

        let pieChartData = pieData;
        if (transformedGraphData.length > 0) {
          let low = 0, medium = 0, high = 0;

          transformedGraphData.forEach(entry => {
            const val = entry.value1;
            if (val < 2.7) low++;
            else if (val >= 2.7 && val <= 3.9) medium++;
            else if (val >= 4) high++;
          });

          const total = low + medium + high;
          const calcPercentage = (count) =>
            total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';

          pieChartData = {
            lowCount: low,
            mediumCount: medium,
            highCount: high,
            lowPercentage: calcPercentage(low),
            mediumPercentage: calcPercentage(medium),
            highPercentage: calcPercentage(high),
          }
        }

        // Rendering
        if (res?.resultcode === SUCCESS) {
          setSummary(summaryData);
          setMeasurement(measurementData);
          setAbove(transformedAboveData);
          setCompare(compareData);
          setGraphData(transformedGraphData);
          setPieData(pieChartData);
        } else {
          throw new Error("API error");
        }

      } catch (error) {
        enqueueSnackbar("Error fetching report details! error: " + error, { variant: "error" });
      } finally {
        setLoading(false); // 데이터 로딩 종료
      }
    };

    loadReportData();
  }, [email, reportId]); // 의존성 배열에 email과 reportId만 넣음

  if (loading) {
    return <Loading />;
  }

  const handleRadonLevelsGuidePopup = () => {
    setOpenRadonLevelsGuideDialog(true);
  }

  const handleWeatherGuidePopup = () => {
    setOpenWeatherGuideDialog(true);
  }

  const surveyUrl = "https://forms.gle/bKocWQrq15YmiSou5";

  return (
    <Container>
      <Dialog open={openRadonLevelsGuideDialog} onClose={handleCloseRadonLevelsGuideDialog} fullWidth maxWidth="sm">
        <RadonLevelsGuideDialog
          handleClose={handleCloseRadonLevelsGuideDialog}
        />
      </Dialog>
      <Dialog open={openWeatherGuideDialog} onClose={handleCloseWeatherGuideDialog} fullWidth maxWidth="sm">
        <WeatherGuideDialog
          handleClose={handleCloseWeatherGuideDialog}
        />
      </Dialog>
      <SurveyTopRow>
        <SurveyTopText variant="body1">Help Us Improve Your Radon Analysis.</SurveyTopText>
        <SurveyTopLink href={surveyUrl} target="_blank" rel="noopener noreferrer">
          Take Survey
        </SurveyTopLink>
      </SurveyTopRow>
      {/* Check if summary.avg72Hour exists (non-null, non-undefined) */}
      {summary.avg72Hour !== undefined && summary.avg72Hour !== null && summary.avg72Hour !== '' ? (
        <>
          {/* Render original content when avg72Hour exists */}


          <Card style={{ marginLeft: 2, marginRight: 2}}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <RecommendTitle
                  variant="h6"
                  style={{
                    color: summary.avg72Hour < 2.7 ? "#4CAF50" :
                      summary.avg72Hour <= 3.9 ? "#FFA726" :
                        "#D32F2F"
                  }}
                >
                  {summary.avg72Hour < 2.7 ? "No Action Required" :
                    summary.avg72Hour <= 3.9 ? "Consider Fixing Home" :
                      "Recommend Fixing Home"}
                </RecommendTitle>
                <InfoButton onClick={() => handleRadonLevelsGuidePopup()}>
                  i
                </InfoButton>
              </div>
              <StyledRow>
                <KeyText variant="body2">Device Name</KeyText>
                <ValueText variant="body2">{summary.deviceName}</ValueText>
              </StyledRow>
              <StyledRow>
                <KeyText variant="body2">72-Hr Radon Average</KeyText>
                <ValueText variant="body2">{summary.avg72Hour} pCi/L</ValueText>
              </StyledRow>
              <StyledRow>
                <KeyText variant="body2">Location Floor</KeyText>
                <ValueText variant="body2">{summary.location}</ValueText>
              </StyledRow>
              <StyledRow>
                <KeyText variant="body2">Mitigation System Installed</KeyText>
                <ValueText variant="body2">{summary.mitigationInstalled}</ValueText>
              </StyledRow>
              <Space height={"20px"} />
              <CustomList>
                {summary.details.map((detail, index) => (
                  <CustomListItem key={index}>
                    <CustomBullet />
                    <CustomText>{detail}</CustomText>
                  </CustomListItem>
                ))}
              </CustomList>
            </CardContent>
          </Card>
          <Card style={{ marginLeft: 2, marginRight: 2, marginTop: 16 }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <SectionTitle variant="h6">Measurement Details</SectionTitle>
                <InfoButton onClick={() => handleWeatherGuidePopup()}>
                  i
                </InfoButton>
              </div>
              <StyledRow>
                <KeyText variant="body2">Start Time</KeyText>
                <ValueText variant="body2">{measurement.measurementStartTime}</ValueText>
              </StyledRow>
              <StyledRow>
                <KeyText variant="body2">End Time</KeyText>
                <ValueText variant="body2">{measurement.measurementEndTime}</ValueText>
              </StyledRow>
              <StyledRow>
                <KeyText variant="body2">Location</KeyText>
                <ValueText variant="body2" style={{ marginLeft: 10, marginRight: 10 }}>{measurement.location}</ValueText>
              </StyledRow>
              <StyledRow>
                <KeyText variant="body2">Average</KeyText>
                <ValueText variant="body2">{measurement.average} pCi/L</ValueText>
              </StyledRow>
              <StyledRow>
                <KeyText variant="body2">Highest</KeyText>
                <ValueText variant="body2">
                  <div>{measurement.highest} pCi/L</div>
                  <div style={{ fontSize: "0.8rem"}}>{measurement.highestTime}</div>
                  {/*<div style={{ fontSize: "0.8rem", color: "#6b7280" }}>{measurement.highestTime}</div>*/}
                </ValueText>
              </StyledRow>

              <StyledRow>
                <KeyText variant="body2">Lowest</KeyText>
                <ValueText variant="body2">
                  <div>{measurement.lowest} pCi/L</div>
                  <div style={{ fontSize: "0.8rem"}}>{measurement.lowestTime}</div>
                  {/*<div style={{ fontSize: "0.8rem", color: "#6b7280" }}>{measurement.lowestTime}</div>*/}
                </ValueText>
              </StyledRow>


            </CardContent>

            <MyRadonLineChart data={graphData} />
          </Card>

          <Card style={{ marginLeft: 2, marginRight: 2, marginTop: 16 }}>
            <CardContent>
              <SectionTitle variant="h6">Date/Time Above 4pCi/L</SectionTitle>
              <Space height={"10px"} />
              {(above && above.length > 0) ? (
                <>
                  <TableContainer expanded={expanded}>
                    {above.map((entry, index) => (
                      <StyledRow key={index}>
                        <KeyText variant="body2">{entry.date} {entry.time}</KeyText>
                        <ValueText variant="body2">{entry.value}</ValueText>
                      </StyledRow>
                    ))}
                  </TableContainer>
                  <ExpandButton onClick={() => setExpanded(!expanded)}>
                    {expanded ? <ExpandLess /> : <ExpandMore />}
                  </ExpandButton>
                </>
              ) : (
                <KeyText variant="body2" style={{ color: "#757575", textAlign: "left" }}>
                  No readings above 4.0 pCi/L were recorded.
                </KeyText>
              )}
            </CardContent>
          </Card>

          <Card style={{ marginLeft: 2, marginRight: 2, marginTop: 16 }}>
            <CardContent>
              <SectionTitle variant="h6">Your Location vs. EPA State Average</SectionTitle>
              <StyledRow>
                <KeyText variant="body2">Your 72-hour avg</KeyText>
                <ValueText variant="body2">{compare.testAvg} pCi/L</ValueText>
              </StyledRow>
              <StyledRow>
                <KeyText variant="body2">{measurement.state} avg</KeyText>
                <ValueText variant="body2">{(compare.stateAvg === 'N/A') ? 'N/A' : compare.stateAvg + ' pCi/L'} </ValueText>
              </StyledRow>
            </CardContent>
          </Card>

          <Card style={{ marginLeft: 2, marginRight: 2, marginTop: 16 }}>
            <CardContent style={{ marginBottom: 0, paddingBottom: 0}}>
              <SectionTitle variant="h6" style={{ marginBottom: 0, paddingBottom: 0}} >Radon Trends</SectionTitle>
            </CardContent>
            <CardContent style={{ textAlign: 'center', paddingTop: 0, marginTop: 0 }}>
              <div
                style={{
                  display: 'flex',
                  gap: '20px',
                  alignItems: 'flex-start', // 세로 방향으로 위쪽에 붙게 설정
                  flexWrap: 'wrap', // 화면이 좁을 때 차트와 텍스트가 줄바꿈 되도록 함
                  justifyContent: 'center', // 차트와 텍스트를 중앙에 배치
                  // backgroundColor: 'red',
                  marginTop: 0, // 추가 여백 제거
                }}
              >
                <div
                  style={{
                    width: '50%',
                    minWidth: 250, // 파이차트가 최소 250px 크기를 가지도록 함
                    display: 'flex',
                    justifyContent: 'center', // 파이차트를 중앙 정렬
                    marginTop: 0, // 파이차트와 위쪽 여백을 0으로 설정
                  }}
                >
                  <PieChart width={250} height={250}>
                    <Pie
                      data={[
                        { name: 'Low', value: pieData.lowCount },
                        { name: 'Medium', value: pieData.mediumCount },
                        { name: 'High', value: pieData.highCount }
                      ]}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="50%" outerRadius={80} fill="#8884d8"
                      label={false} // 레이블을 비활성화하여 단위가 표시되지 않도록 함
                    >
                      <Cell fill="#4CAF50" /> {/* Low */}
                      <Cell fill="#FFA726" /> {/* Medium */}
                      <Cell fill="#D32F2F" /> {/* High */}
                    </Pie>
                    {/*<Legend />*/}
                  </PieChart>
                </div>
                <div
                  style={{
                    width: '50%',
                    minWidth: 200, // 최소 너비를 설정하여 글씨가 너무 좁지 않게 함
                  }}
                >
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, justifyContent: 'center' }}>
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          backgroundColor: '#D32F2F', // Low 색상
                          borderRadius: 0,
                          marginRight: 25,
                        }}
                      ></div>
                      <Typography variant="body2">≥ 4 pCi/L</Typography>
                      <Typography variant="body2" style={{ marginLeft: 'auto', fontWeight: 500, paddingRight: 20 }}>
                        {pieData.highPercentage}%
                      </Typography>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, justifyContent: 'center' }}>
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          backgroundColor: '#FFA726', // Medium 색상
                          borderRadius: 0,
                          marginRight: 25,
                        }}
                      ></div>
                      <Typography variant="body2">2.7-3.9 pCi/L</Typography>
                      <Typography variant="body2" style={{ marginLeft: 'auto', fontWeight: 500, paddingRight: 20 }}>
                        {pieData.mediumPercentage}%
                      </Typography>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, justifyContent: 'center' }}>
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          backgroundColor: '#4CAF50', // High 색상
                          borderRadius: 0,
                          marginRight: 25,
                        }}
                      ></div>
                      <Typography variant="body2">&lt; 2.7 pCi/L</Typography>
                      <Typography variant="body2" style={{ marginLeft: 'auto', fontWeight: 500, paddingRight: 20 }}>
                        {pieData.lowPercentage}%
                      </Typography>
                    </div>
                  </div>
                  <Typography
                    variant="body2"
                    style={{
                      marginTop: 12,
                      color: '#666',
                      fontSize: 12,
                    }}
                  >
                    EPA Action Guideline: 4 pCi/L
                  </Typography>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card style={{ marginLeft: 2, marginRight: 2, marginTop: 16 }}>
            <CardContent>
              <SurveyTitle variant="h5">Help Us Improve Your Radon Analysis</SurveyTitle>
              <SurveyDescription variant="body1">
                Tell us what would make this report and future analysis features more useful.
                {" "}
                One-time survey. Takes less than 1 minute.
              </SurveyDescription>
              <SurveyButton
                variant="contained"
                component="a"
                href={surveyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Take Survey
              </SurveyButton>
            </CardContent>
          </Card>
          <Card style={{ marginLeft: 2, marginRight: 2, marginTop: 16 }}>
            <CardContent style={{ marginTop: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0 }}>
              <Space height={"10px"} />
              <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: 15}}>
                {reportId && `Analysis ID: ${reportId.replace('MRR-', '').slice(-6)}`}
              </Typography>
              <Space height={"10px"} />
            </CardContent>
          </Card>
          <Card style={{ marginLeft: 2, marginRight: 2, marginTop: 16 }}>
            <CardContent>
              <SectionTitle variant="h6">Disclaimer</SectionTitle>
              <Typography variant="body2">
                {disclaimer}
              </Typography>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card style={{ marginLeft: 2, marginRight: 2, marginTop: 16 }}>
          <CardContent>
            <Typography variant="h6" style={{ textAlign: 'center', color: '#808080', fontSize: '14px' }}>
              {reportId
                ? `An issue occurred while loading the report data using Report ID (${reportId}). Please try again.`
                : "There's no passed Report ID. Please try again."
              }
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}

export default Report;

